//@flow

// Vendors
import React, { useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import classnames from 'classnames';

// Actions
import { closeModal } from '../../../../reducers/modal/modalActions';

// Components
import { InputRadio, FormActions, FormActionsItem, Trans } from '../..';
import { Modal } from '../../modals';
import { ButtonPrimary } from 'texkit/dist/components/Buttons';

// Helpersid
import {
  formatAddressForVerifyAddressModal,
  truncateDMVAddress,
} from '../../../../lib/helpers';

// Styles
import './verify-address-modal.scss';
import { states, counties } from '../../../../lib/data/agencyData/BON';

// Types
type Props = {
  id?: string,
  onCancel?: Function,
  onSuccess: Function,
  modal: Object,
  closeModal: Function,
  disableCloseButton?: boolean,
  className?: string,
};

function VerifyAddressModal(props: Props) {
  const {
    className,
    onSuccess,
    closeModal,
    id = 'VerifyAddressModal',
    modal: { data },
    disableCloseButton,
    ...rest
  } = props;

  if (!data || !data.addressOptions || !data.enteredAddress) return false;
  const enteredAddress =
    data.enteredAddress.state && data.enteredAddress.state.value
      ? {
          ...data.enteredAddress,
          state: data.enteredAddress.state.value,
        }
      : data.enteredAddress;

  const isDmvAddress = /DMV/.test(id);
  const addressOptions = isDmvAddress
    ? data.addressOptions.map(truncateDMVAddress)
    : data.addressOptions;
  const addressOptionWasTruncated = data.addressOptions.map(
    (original, index) => {
      const truncated = addressOptions[index];
      return (
        isDmvAddress &&
        ['city', 'streetAddress', 'addressLine2'].some(
          field => original[field] !== truncated[field]
        )
      );
    }
  );
  const displayAddressTruncationMsg = addressOptionWasTruncated.some(Boolean);

  const [selectedAddress, setSelectedAddress] = useState(
    addressOptions[0] || null
  );

  function handleChange(event) {
    const { name, value } = event.target;
    if (name && value) setSelectedAddress(JSON.parse(value));
  }

  function handleSuccess() {
    closeModal(id);
    onSuccess({
      ...selectedAddress,
      verifySkip: true,
      state:
        states.find(state => state.value === selectedAddress.state) || null,
      county:
        counties.find(county => county.label === selectedAddress.county) ||
        null,
    });
    setSelectedAddress(addressOptions[0] || null);
  }

  return (
    <Modal
      {...rest}
      id={id}
      disableOnClickOutside={true}
      className={classnames('verify-address-modal', className)}
      disableCloseButton={!disableCloseButton ? false : true}
    >
      <h1 className="h1 modal-title">
        <Trans
          file="Modals"
          id="ConfirmCorrectAddress"
          fallback="Let’s make sure your address is correct"
        />
      </h1>
      <div className="modal-content">
        <div className="verify-address-modal-input-wrapper">
          <div className="verify-address-modal-label">
            <div className="address-label">
              {data.addressOptions.length > 1 ? (
                <Trans
                  file="Labels"
                  id={
                    /DMV/.test(id)
                      ? 'SuggestedAddresses_star'
                      : 'SuggestedAddresses'
                  }
                  fallback="SuggestedAddresses:"
                />
              ) : (
                <Trans
                  file="Labels"
                  id={
                    /DMV/.test(id)
                      ? 'SuggestedAddress_star'
                      : 'SuggestedAddress'
                  }
                  fallback="Suggested address:"
                />
              )}
            </div>
            <div>
              {addressOptions.map((address, index) => {
                return (
                  <InputRadio
                    key={`verifyAddress${index}`}
                    id={`verifyAddress${index}`}
                    name="verifyAddress"
                    className={
                      addressOptionWasTruncated[index]
                        ? 'dmv-address-truncated'
                        : ''
                    }
                    onChange={handleChange}
                    label={formatAddressForVerifyAddressModal(
                      address,
                      addressOptionWasTruncated[index]
                    )}
                    value={JSON.stringify(address)}
                    checked={
                      JSON.stringify(address) ===
                      JSON.stringify(selectedAddress)
                    }
                  />
                );
              })}
            </div>
          </div>
        </div>
        <hr></hr>
        <div className="verify-address-modal-input-wrapper">
          <div className="verify-address-modal-label">
            <div className="address-label">
              <Trans
                file="Modals"
                id={
                  /DMV/.test(id)
                    ? 'UseAddressEntered_star'
                    : 'UseAddressEntered'
                }
                fallback="Address you entered:"
              />
            </div>
            <div>
              <InputRadio
                id="enteredAddress"
                name="verifyAddress"
                onChange={handleChange}
                label={formatAddressForVerifyAddressModal(enteredAddress)}
                value={JSON.stringify(enteredAddress)}
                checked={
                  JSON.stringify(enteredAddress) ===
                  JSON.stringify(selectedAddress)
                }
              />
            </div>
          </div>
          {/DMV/.test(id) && displayAddressTruncationMsg && (
            <div className="address-truncation-msg">
              <Trans
                file="dmv"
                id="dmvrr_address_truncation_msg"
                fallback="*We shortened your address due to TxDMV requirements. Don't worry, this shouldn't affect the delivery of your sticker."
              />
            </div>
          )}
        </div>
        <FormActions align="center">
          <FormActionsItem>
            <ButtonPrimary onClick={handleSuccess}>
              <Trans
                file="Labels"
                id="UpdateAddress"
                fallback="Use this address"
              />
            </ButtonPrimary>
          </FormActionsItem>
        </FormActions>
      </div>
    </Modal>
  );
}

const mapStateToProps = ({ modal }): Object => ({ modal });
const mapDispatchToProps = (dispatch: Object) =>
  bindActionCreators({ closeModal }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(VerifyAddressModal);
